/**
 * ABN/ACN Mock Data Configuration
 * 
 * This file contains configurable mock data for ABN/ACN lookups.
 * You can easily add, remove, or modify test companies here without
 * changing the lookup service logic.
 * 
 * Format:
 * - Each entry must have a valid 11-digit ABN
 * - ACN is automatically derived from ABN (last 9 digits)
 * - Status should be 'Active' or 'Inactive'
 * - All fields are optional except abn and name
 */

export interface ConfiguredABNData {
  abn: string;
  name: string;
  status?: 'Active' | 'Inactive' | 'Suspended';
  businessType?: string;
  address?: string;
}

/**
 * Configured ABN/ACN data for mock lookups
 * Add your test companies here
 */
export const CONFIGURED_ABN_DATA: ConfiguredABNData[] = [
  {
    abn: '50110219460',
    name: 'Google Australia Pty Ltd',
    status: 'Active',
    businessType: 'Company',
    address: 'Level 5, 48 Pirrama Road, Pyrmont NSW 2009',
  },
  {
    abn: '84096304620',
    name: 'Facebook Australia Pty Limited',
    status: 'Active',
    businessType: 'Company',
    address: '20 Miller Street, North Sydney NSW 2060',
  },
  {
    abn: '11087396016',
    name: 'Amazon Australia Services Pty Ltd',
    status: 'Active',
    businessType: 'Company',
    address: 'Unit 3a, 72 O\'Riordan Road, Alexandria NSW 2015',
  },
];

/**
 * Simulation delay (in milliseconds) for mock lookups
 * This mimics network latency for better UX testing
 * Adjust this value as needed
 */
export const LOOKUP_SIMULATION_DELAY = 800;

/**
 * Backend API configuration
 * Uses live ABR (Australian Business Register) lookup API
 */
export const BACKEND_API_CONFIG = {
  // KYB Lookup API - AWS Lambda endpoint (handles both ABN and ACN)
  KYB_URL: (import.meta.env.VITE_KYB_URL as string) || 'https://7hevjlaqmg.execute-api.ap-southeast-2.amazonaws.com/Prod/kyb',
  TIMEOUT_MS: 15000,
  ENABLE_MOCK_FALLBACK: true, // Fall back to mock data if backend fails
};

/**
 * Convert ABN to ACN
 * ACN is the last 9 digits of the ABN
 */
export function abnToAcn(abn: string): string {
  const cleanAbn = abn.replace(/\D/g, '');
  if (cleanAbn.length !== 11) {
    return '';
  }
  return cleanAbn.slice(2);
}

/**
 * Validate ABN format (11 digits)
 */
export function isValidABNFormat(abn: string): boolean {
  const cleanAbn = abn.replace(/\D/g, '');
  return cleanAbn.length === 11;
}

/**
 * Validate ACN format (9 digits)
 */
export function isValidACNFormat(acn: string): boolean {
  const cleanAcn = acn.replace(/\D/g, '');
  return cleanAcn.length === 9;
}

/**
 * Find configured ABN data
 */
export function findConfiguredABN(abn: string): ConfiguredABNData | undefined {
  const cleanAbn = abn.replace(/\D/g, '');
  return CONFIGURED_ABN_DATA.find(entry => entry.abn === cleanAbn);
}

/**
 * Find configured ACN data (by converting ACN to ABN)
 */
export function findConfiguredACN(acn: string): ConfiguredABNData | undefined {
  const cleanAcn = acn.replace(/\D/g, '');
  return CONFIGURED_ABN_DATA.find(entry => {
    const entryAcn = abnToAcn(entry.abn);
    return entryAcn === cleanAcn;
  });
}
